<script setup lang="ts">
import { Permissions } from '@package/jwt/nuxt'
import { splitByCase, titleCase } from 'scule'
import {
  CONTENT_DATA_QUERY_KEYS,
  POLICY_QUERY_KEYS,
} from '~/queries/paapi/content-data'

const { session } = useUserSession()

const { data: config } = useFetch('/api/config')

const enableSystemItems = computed(() =>
  session.value?.permissions?.includes(Permissions.writeSystem),
)
const enableUsers = computed(() =>
  session.value?.permissions?.includes(Permissions.readUsersAll),
)
const enableWorkspaces = computed(() =>
  session.value?.permissions?.includes(Permissions.readWorkspacesAll),
)

const { isFetching: policiesFetching } = useQueryStateExtended(
  POLICY_QUERY_KEYS.root,
)
const { isFetching: refDocsFetching } = useQueryStateExtended(
  CONTENT_DATA_QUERY_KEYS.referenceDocuments,
)
const pendingIcon = {
  trailingIcon: 'i-svg-spinners-3-dots-fade',
}

const { data: docsItems } = useAsyncData(
  'navigation-menu/docs/all.json',
  async () => {
    const result = await queryCollectionNavigation('docs')
    return result.map((item) => ({
      label: item.title,
      to: `/docs${item.path}`,
    }))
  },
)

const { data: testItems } = useAsyncData(
  'navigation-menu/tests/all.json',
  async () => {
    const result = await queryCollection('tests')
      .select('id', 'success', 'meta')
      .all()
    return result.map((item) => ({
      id: item.id,
      success: item.success,
      label: titleCase(splitByCase(item.meta?.path as string)),
      trailingIcon: item.success ? 'i-ph-check-circle' : 'i-ph-x-circle',
      to: `/tests${item.meta?.path}`,
    }))
  },
)

const items = computed(() => [
  [
    {
      label: 'Home',
      icon: 'i-ph-house',
      to: '/dashboard',
    },

    ...(enableUsers.value
      ? [
          {
            label: 'Users',
            icon: 'i-ph-users',
            to: '/users',
          },
          {
            label: 'Organizations',
            icon: 'i-ph-building-office',
            to: '/organizations',
          },
        ]
      : []),
    ...(enableWorkspaces.value
      ? [
          {
            label: 'Workspaces',
            icon: 'i-ph-squares-four',
            to: '/workspaces',
          },
        ]
      : []),
  ],
  [
    {
      label: 'Scoring Algorithm Tool',
      icon: 'i-ph-screwdriver',
      children: [
        {
          label: 'Categories & Attributes',
          to: '/content-tools/scoring-algorithm/categories',
        },
        {
          label: 'Configuration',
          to: '/content-tools/scoring-algorithm/configuration',
        },
      ],
    },
  ],
  ...(config.value?.analyticsEngineAvailable
    ? [
        [
          {
            label: 'Analytics',
            icon: 'i-ph-chart-bar',
            to: '/analytics',
          },
        ],
      ]
    : []),
  [
    {
      label: 'Content Library',
      icon: 'i-ph-books',
      children: [
        {
          label: 'Policies',
          to: '/content-library/policies',
          ...(policiesFetching.value ? pendingIcon : {}),
        },
        {
          label: 'Sources',
          to: '/content-library/reference-documents',
          ...(refDocsFetching.value ? pendingIcon : {}),
        },
        {
          label: 'Engines',
          to: '/content-library/engines',
        },
        {
          label: 'Logic Types',
          to: '/content-library/logic-types',
        },
        {
          label: 'Ref data',
          to: '/content-library/ref-data/diagnosis-codes',
        },
        {
          label: 'Source Types',
          to: '/content-library/source-types/level-one',
        },
        {
          label: 'Topics',
          to: '/content-library/topics',
        },
      ],
    },
  ],
  [
    {
      label: 'Content Tools',
      icon: 'i-ph-wrench',
      children: [
        {
          label: 'Claims Testing',
          to: '/content-tools/claims-testing',
        },
        {
          label: 'Predicate Search',
          to: '/content-tools/predicate-search',
        },
        {
          label: 'Document Scoping',
          to: '/content-tools/scoping',
        },
        {
          label: 'Claims Finder',
          to: '/content-tools/claims-finder',
        },
        {
          label: 'Policy Builder',
          to: '/content-tools/policy-builder',
        },
        {
          label: 'Drug Policy Builder',
          to: '/content-tools/drug-builder',
        },
      ],
    },
  ],
  [
    {
      label: 'APIs',
      icon: 'i-ph-atom',
      children: [
        {
          label: 'PaAPI',
          to: '/apis/paapi',
        },
      ],
    },
  ],
  [
    {
      label: 'Architecture',
      icon: 'i-ph-graph',
      to: '/architecture',
    },
  ],

  /* LOCAL DEVELOPMENT ONLY */
  ...(import.meta.dev
    ? [
        [
          {
            label: 'Teams',
            icon: 'i-ph-users-four',
            children: [
              {
                label: 'Cloud',
                to: '/cloud',
              },
              {
                label: 'Core Claims',
                to: '/claims',
              },
              {
                label: 'Data',
                to: '/data',
              },
              {
                label: 'Engines',
                to: '/engines',
              },
              {
                label: 'Workflows',
                to: '/workflows',
              },
            ],
          },
        ],
        [
          {
            label: 'Releases',
            icon: 'i-ph-package',
            to: '/releases',
          },
        ],
      ]
    : []),
  [
    {
      label: 'Documentation',
      icon: 'i-ph-book-open-text',
      children: docsItems.value,
    },
  ],
  [
    {
      label: 'Tests',
      icon: 'i-ph-test-tube',
      children: testItems.value,
    },
  ],
  ...(enableSystemItems.value
    ? [
        [
          {
            label: 'System',
            icon: 'i-ph-faders-horizontal',
            to: '/system',
          },
        ],
      ]
    : []),
  [
    {
      label: 'Imports',
      icon: 'i-ph-download-simple',
      children: [
        {
          label: 'Client Review',
          to: '/imports/client-review-status',
        },
        {
          label: 'MCD Provider Types',
          to: '/imports/mcd-provider-types',
        },
      ],
    },
  ],
])
</script>

<template>
  <UNavigationMenu
    :items="items"
    highlight
    highlight-color="primary"
    orientation="vertical"
    variant="pill"
  />
</template>
